import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export default function PaymentContractModal({
  isOpen,
  onClose,
  client,
  onSave,
}) {
  // Helper function to format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState({
    contract: {
      signed: client?.contract?.signed || false,
      signedDate: formatDateForInput(client?.contract?.signedDate),
      documentUrl: client?.contract?.documentUrl || "",
    },
    defaultPaymentMethod: client?.defaultPaymentMethod
      ? {
          ...client.defaultPaymentMethod,
          expiryDate: formatDateForInput(
            client.defaultPaymentMethod.expiryDate
          ),
        }
      : null,
    creditCardOnFile: client?.creditCardOnFile || false,
    hasPaymentMethod: Boolean(client?.defaultPaymentMethod?.name),
    splitBilling: {
      enabled: client?.splitBilling?.enabled || false,
      parentId: client?.splitBilling?.parentId || "",
      instructions: client?.splitBilling?.instructions || "",
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Only include payment method data if hasPaymentMethod is true
    const dataToSave = {
      ...formData,
      defaultPaymentMethod: formData.hasPaymentMethod
        ? formData.defaultPaymentMethod
        : null,
    };
    onSave(dataToSave);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      // Special handling for hasPaymentMethod checkbox
      if (name === "hasPaymentMethod") {
        setFormData((prev) => ({
          ...prev,
          hasPaymentMethod: checked,
          defaultPaymentMethod: checked
            ? {
                name: "Credit Card",
                lastFourDigits: "",
                expiryDate: "",
              }
            : null,
        }));
      } else if (name === "splitBillingEnabled") {
        // Handle split billing enable/disable
        setFormData((prev) => ({
          ...prev,
          splitBilling: {
            ...prev.splitBilling,
            enabled: checked,
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: type === "checkbox" ? checked : value,
        }));
      }
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-xl rounded-sm bg-white p-4 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Payment & Contract Information
            </Dialog.Title>
            <button
              onClick={onClose}
              className="rounded-sm p-1 hover:bg-gray-100 transition-colors">
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Contract Information */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">
                Contract Information
              </h3>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="contract.signed"
                  name="contract.signed"
                  checked={formData.contract.signed}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <label
                  htmlFor="contract.signed"
                  className="text-sm text-gray-700">
                  Contract Signed
                </label>
              </div>
              {formData.contract.signed && (
                <div className="space-y-3 pl-6">
                  <div>
                    <label
                      htmlFor="contract.signedDate"
                      className="block text-sm font-medium text-gray-700 mb-1">
                      Signed Date
                    </label>
                    <input
                      type="date"
                      id="contract.signedDate"
                      name="contract.signedDate"
                      value={formData.contract.signedDate}
                      onChange={handleChange}
                      className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contract.documentUrl"
                      className="block text-sm font-medium text-gray-700 mb-1">
                      Document URL
                    </label>
                    <input
                      type="text"
                      id="contract.documentUrl"
                      name="contract.documentUrl"
                      value={formData.contract.documentUrl}
                      onChange={handleChange}
                      className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Payment Information */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Payment Information</h3>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="hasPaymentMethod"
                  name="hasPaymentMethod"
                  checked={formData.hasPaymentMethod}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <label
                  htmlFor="hasPaymentMethod"
                  className="text-sm text-gray-700">
                  Has Default Payment Method
                </label>
              </div>

              {formData.hasPaymentMethod && (
                <div className="space-y-3 pl-6">
                  <div>
                    <label
                      htmlFor="defaultPaymentMethod.name"
                      className="block text-sm font-medium text-gray-700 mb-1">
                      Default Payment Method
                    </label>
                    <select
                      id="defaultPaymentMethod.name"
                      name="defaultPaymentMethod.name"
                      value={formData.defaultPaymentMethod.name}
                      onChange={handleChange}
                      className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
                      <option value="Credit Card">Credit Card</option>
                      <option value="Debit Card">Debit Card</option>
                      <option value="E-Transfer">E-Transfer</option>
                      <option value="Cash">Cash</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  {formData.defaultPaymentMethod.name.includes("Card") && (
                    <>
                      <div>
                        <label
                          htmlFor="defaultPaymentMethod.lastFourDigits"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Last Four Digits
                        </label>
                        <input
                          type="text"
                          id="defaultPaymentMethod.lastFourDigits"
                          name="defaultPaymentMethod.lastFourDigits"
                          value={formData.defaultPaymentMethod.lastFourDigits}
                          onChange={handleChange}
                          maxLength="4"
                          className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="defaultPaymentMethod.expiryDate"
                          className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Date
                        </label>
                        <input
                          type="date"
                          id="defaultPaymentMethod.expiryDate"
                          name="defaultPaymentMethod.expiryDate"
                          value={formData.defaultPaymentMethod.expiryDate}
                          onChange={handleChange}
                          className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="creditCardOnFile"
                  name="creditCardOnFile"
                  checked={formData.creditCardOnFile}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
                <label
                  htmlFor="creditCardOnFile"
                  className="text-sm text-gray-700">
                  Credit Card on File
                </label>
              </div>
            </div>

            {/* Split Billing */}
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">Split Billing</h3>

              {/* Radio buttons for Yes/No */}
              <div className="flex gap-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="splitBillingYes"
                    name="splitBillingEnabled"
                    checked={formData.splitBilling.enabled === true}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      splitBilling: {
                        ...prev.splitBilling,
                        enabled: true
                      }
                    }))}
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <label
                    htmlFor="splitBillingYes"
                    className="ml-2 text-sm text-gray-700">
                    Yes
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="splitBillingNo"
                    name="splitBillingEnabled"
                    checked={formData.splitBilling.enabled === false}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      splitBilling: {
                        ...prev.splitBilling,
                        enabled: false
                      }
                    }))}
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <label
                    htmlFor="splitBillingNo"
                    className="ml-2 text-sm text-gray-700">
                    No
                  </label>
                </div>
              </div>

              {/* Instructions / Notes - Always visible */}
              <div>
                <label
                  htmlFor="splitBilling.instructions"
                  className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions / Notes
                </label>
                <textarea
                  id="splitBilling.instructions"
                  name="splitBilling.instructions"
                  value={formData.splitBilling.instructions}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter any split billing instructions or notes here..."
                  className="block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm resize-none"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-sm bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                Save Changes
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
